import { startCase, toLower } from 'lodash-es';

import { Injectable } from '@angular/core';

import { SnAlarm } from '@lib/api/entities';
import { t } from '@lib/translation';

/**
 * Interface for bounding box coordinates and dimensions
 */
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Interface for standardized ALPR details returned by the service
 */
export interface AlprDetails {
  // LICENSE PLATE DATA
  plate: string | null;
  state: string | number | null;
  reason: string | null;

  // VEHICLE DATA
  make: string | number | null;
  model: string | number | null;
  color: string | number | null;
  type: string | number | null;
  year: string | number | null;

  // CONFIDENCE & CAMERA INFO
  confidence: string | number | null;
  cameraName: string | null;

  // IMAGES
  contextImage: string | null;
  plateImage: string | null;

  // ADDITIONAL DATA
  speed: string | number | null;
  direction: string | null;
  provider: string | null;

  // CONFIDENCE FIELDS (Fusus/Flock/Axon specific)
  plateStateConfidence: string | number | null;
  vehicleMakeConfidence: string | number | null;
  vehicleYearConfidence: string | number | null;
  vehicleColorConfidence: string | number | null;
  vehicleModelConfidence: string | number | null;
  vehicleBodyTypeConfidence: string | number | null;

  // BOUNDING BOX DATA
  plateBoundingBox: BoundingBox | null;
  imageCoordinates: BoundingBox | null;
}

/**
 * Centralized service for detecting and categorizing ALPR alarms.
 * Handles all ALPR providers and types consistently across the application.
 */
@Injectable({
  providedIn: 'root',
})
export class AlprDetectionService {
  // Memoization cache for getAlprDetails with LRU eviction
  private readonly alprDetailsCache = new Map<string, AlprDetails>();
  private readonly maxCacheSize = 100;

  // All known ALPR alarm types
  private readonly alprTypes = new Set([
    'RT ALPR',
    'FS ALPR',
    'GENETEC ALPR',
    'GENETEC_ALPR',
    'GENETEC_SHARPV_ALPR',
    'GENETEC_SHARP3_ALPR',
    'REKOR_ALPR',
    'VIGILANT_LEARN_ALPR',
    'ELSAQ_ALPR',
    'EPIC_IO_LPR',
    'EPIC_IO_ALPR',
    'NDI_ALPR',
    'WC_ALPR',
    'ZEROEYES_ALPR',
    'INTERNAL_LPR',
    'LOGIQ_LPR',
    'LPR',
    'ALPR',
    'MINUTEMAN', // Often LPR-related
  ]);

  // All known ALPR providers
  private readonly alprProviders = new Set([
    'FLOCK_SAFETY',
    'REKOR',
    'VIGILANT',
    'GENETEC',
    'GENETEC_SHARP3',
    'GENETEC_SHARP5',
    'AXON_FLEET',
    'FUSUS',
    'ELSAG',
    'EPIC_IO',
    'NDI',
    'LOGIQ',
    'MINUTEMAN',
    'OPENALPR',
    'VAXTOR',
    'VERKADA',
    'WATCHMAN',
    'WOLFCOM',
    'RHOMBUS',
    'ALTUMINT',
    'BLUE_LINE',
    'EAGLE_EYE_NETWORKS',
    'GEOVISION',
    'INSIGHT_LPR',
    'MILESTONE',
    'BOSCH',
    'SKYCOP',
  ]);

  /**
   * Determines if an alarm is ALPR-related based on type and provider.
   */
  public isAlprAlarm(alarm: SnAlarm): boolean {
    // Handle null/undefined alarm
    if (!alarm || !alarm.type) {
      return false;
    }

    // Check by exact alarm type match
    if (this.alprTypes.has(alarm.type)) {
      return true;
    }

    // Check by provider
    if (alarm.details?.provider && this.alprProviders.has(alarm.details.provider)) {
      return true;
    }

    // Check by type pattern (contains 'alpr', 'lpr', etc.)
    const typePattern = /\b(alpr|lpr)\b/i;
    return typePattern.test(alarm.type);
  }

  /**
   * Gets the ALPR provider for an alarm.
   */
  getAlprProvider(alarm: SnAlarm): string | null {
    // Check direct provider field first
    if (alarm.details?.provider) {
      return alarm.details.provider;
    }

    // Check alarm source name for provider clues (e.g. "Fusus ALPR Camera")
    if (alarm.source && 'name' in alarm.source && alarm.source.name) {
      const sourceName = alarm.source.name.toUpperCase();
      if (sourceName.includes('FUSUS')) return 'FUSUS';
      if (sourceName.includes('GENETEC')) return 'GENETEC';
      if (sourceName.includes('AXON') || sourceName.includes('FLEET')) return 'AXON_FLEET';
      if (sourceName.includes('FLOCK')) return 'FLOCK_SAFETY';
      if (sourceName.includes('VIGILANT')) return 'VIGILANT';
      if (sourceName.includes('REKOR')) return 'REKOR';
    }

    // Check for provider patterns in the data structure
    const details = alarm.details || {};
    if (details.Plate || details.PlateImage || details.ContextImage) {
      return 'GENETEC'; // XML format typical of Genetec
    }
    if (details.vehicle_make || details.plate_read || details.context_image) {
      return 'AXON_FLEET'; // Axon Fleet specific fields
    }

    return null;
  }

  /**
   * Gets user-friendly display name for ALPR provider.
   */
  getProviderDisplayName(alarm: SnAlarm): string {
    const provider = this.getAlprProvider(alarm);

    if (provider) {
      switch (provider.toUpperCase()) {
        case 'FUSUS':
          return t('common.fusus');
        case 'GENETEC':
          return t('alpr.providers.genetec');
        case 'AXON_FLEET':
        case 'AXON':
          return t('alpr.providers.axon');
        case 'FLOCK_SAFETY':
        case 'FLOCK':
          return t('alpr.providers.flockSafety');
        case 'VIGILANT':
          return t('alpr.providers.vigilant');
        case 'REKOR':
        case 'OPENALPR':
          return t('alpr.providers.rekor');
        case 'RT_ALPR':
        case 'RT':
          return t('alpr.providers.rtAlpr');
        case 'VERKADA':
          return t('common.verkada');
        case 'EAGLE_EYE_NETWORKS':
          return t('alpr.providers.eagleEyeNetworks');
        default:
          return provider; // Use the raw provider string
      }
    }

    return t('common.unknown');
  }

  /**
   * Gets the license plate from alarm details.
   * Handles different field naming conventions across providers.
   */
  getLicensePlate(alarm: SnAlarm): string | null {
    // Handle null/undefined alarm
    if (!alarm || !alarm.details) {
      return null;
    }

    return (
      alarm.details.Plate ||
      alarm.details.plate ||
      alarm.details.license_plate ||
      alarm.details.plateNumber ||
      alarm.details.plate_read || // Axon Fleet
      alarm.details.licensePlate || // Flock Safety
      null
    );
  }

  /**
   * Filters out null values, empty strings, and "NULL" strings from ALPR data.
   */
  private cleanAlprValue(value: unknown): string | number | null {
    if (
      value === null ||
      value === undefined ||
      value === '' ||
      value === 'NULL' ||
      value === 'null'
    ) {
      return null;
    }
    return value as string | number;
  }

  /**
   * Gets standardized ALPR details for display.
   * Uses memoization to cache results for improved performance.
   */
  getAlprDetails(alarm: SnAlarm): AlprDetails {
    // Handle null/undefined alarm
    if (!alarm || !alarm.id) {
      return {
        plate: null,
        state: null,
        reason: null,
        make: null,
        model: null,
        color: null,
        type: null,
        year: null,
        cameraName: null,
        confidence: null,
        contextImage: null,
        plateImage: null,
        speed: null,
        direction: null,
        provider: null,
        plateStateConfidence: null,
        vehicleMakeConfidence: null,
        vehicleYearConfidence: null,
        vehicleColorConfidence: null,
        vehicleModelConfidence: null,
        vehicleBodyTypeConfidence: null,
        plateBoundingBox: null,
        imageCoordinates: null,
      };
    }

    const cacheKey = alarm.id;

    // Check cache first
    if (this.alprDetailsCache.has(cacheKey)) {
      // Move to end for LRU (most recently used)
      const cachedResult = this.alprDetailsCache.get(cacheKey);
      if (cachedResult) {
        this.alprDetailsCache.delete(cacheKey);
        this.alprDetailsCache.set(cacheKey, cachedResult);
        return cachedResult;
      }
    }

    // Compute result using existing logic
    const result = this.computeAlprDetails(alarm);

    // Cache the result with LRU eviction
    this.cacheAlprDetailsResult(cacheKey, result);

    return result;
  }

  /**
   * Clears the memoization cache for a specific alarm ID.
   * Useful when alarm data is updated.
   */
  clearAlprDetailsCache(alarmId: string): void {
    this.alprDetailsCache.delete(alarmId);
  }

  /**
   * Clears the entire ALPR details cache.
   */
  clearAllAlprDetailsCache(): void {
    this.alprDetailsCache.clear();
  }

  /**
   * Gets the camera source/type for an alarm with provider-specific logic.
   */
  getCameraSource(alarm: SnAlarm): string {
    // Use the proper provider detection
    const provider = this.getAlprProvider(alarm);

    if (provider) {
      switch (provider.toUpperCase()) {
        case 'FUSUS':
          return t('alpr.cameraTypes.fusus');
        case 'GENETEC':
          return t('alpr.cameraTypes.genetec');
        case 'AXON_FLEET':
        case 'AXON':
          return t('alpr.cameraTypes.axon');
        case 'FLOCK_SAFETY':
        case 'FLOCK':
          return t('alpr.cameraTypes.flockSafety');
        case 'VIGILANT':
          return t('alpr.cameraTypes.vigilant');
        case 'REKOR':
        case 'OPENALPR':
          return t('alpr.cameraTypes.rekor');
        case 'RT_ALPR':
        case 'RT':
          return t('alpr.cameraTypes.rtAlpr');
        case 'VERKADA':
          return t('alpr.cameraTypes.verkada');
        case 'EAGLE_EYE_NETWORKS':
          return t('alpr.cameraTypes.eagleEyeNetworks');
        default:
          return `${provider} ${t('alpr.cameraTypes.unknown')}`;
      }
    }

    // Fallback to device type or source info if no provider
    const deviceType =
      alarm?.details?.deviceType ||
      alarm?.details?.DeviceType ||
      alarm?.details?.['Device Type'] ||
      alarm?.sourceInfo?.name;

    return deviceType || t('alpr.sources.unknownSource');
  }

  /**
   * Checks if the alarm is from an Axon provider.
   */
  isAxonProvider(alarm: SnAlarm): boolean {
    const alarmType = alarm?.type?.toLowerCase() || '';
    const deviceType = alarm?.details?.deviceType?.toLowerCase() || '';

    // Check both alarm type and device type for Axon providers
    return (
      alarmType.includes('axon') ||
      alarmType.includes('fleet') ||
      deviceType.includes('axon') ||
      deviceType.includes('outpost') ||
      deviceType.includes('fleet') ||
      deviceType.includes('lightpost')
    );
  }

  /**
   * Extracts the Axon provider type (Outpost, Fleet, Lightpost).
   */
  getAxonProviderType(alarm: SnAlarm): string {
    const alarmType = alarm?.type?.toLowerCase() || '';
    const deviceType = alarm?.details?.deviceType?.toLowerCase() || '';

    // Check alarm type first (for Fleet alarms which have it in the type)
    if (alarmType.includes('fleet') || deviceType.includes('fleet')) {
      return t('alpr.deviceTypes.fleet');
    } else if (deviceType.includes('outpost')) {
      return t('alpr.deviceTypes.outpost');
    } else if (deviceType.includes('lightpost')) {
      return t('alpr.deviceTypes.lightpost');
    }

    // Default fallback for other Axon providers
    return t('alpr.deviceTypes.fleet');
  }

  /**
   * Gets the priority level from alarm details.
   */
  getPriorityLevel(alarm: SnAlarm): string {
    const priority = alarm?.details?.priority || '';

    // Ensure proper capitalization
    if (priority.toLowerCase() === 'high') return t('alpr.priorities.high');
    if (priority.toLowerCase() === 'medium') return t('common.medium');
    if (priority.toLowerCase() === 'low') return t('alpr.priorities.low');

    // Default to Normal if not specified
    return t('common.normal');
  }

  /**
   * Gets the dynamic title for the ALPR alert based on provider and priority.
   */
  getAlprTitle(alarm: SnAlarm): string {
    if (!alarm) {
      return t('alpr.alertTypes.alprAlert');
    }

    const provider = this.getAlprProvider(alarm);

    // Special handling ONLY for Axon providers with priority levels
    if (provider === 'AXON' || provider === 'AXON_FLEET') {
      const providerType = this.getAxonProviderType(alarm);
      const priority = this.getPriorityLevel(alarm);
      return t('alpr.alertTypes.axonPriorityHit', { providerType, priority });
    }

    // All other providers use generic title
    return t('alpr.alertTypes.hotlistHit');
  }

  /**
   * Extracts and formats hotlist name with title case.
   */
  getHotlistName(alarm: SnAlarm): string {
    const hotlistName =
      alarm?.details?.list ||
      alarm?.details?.List ||
      alarm?.details?.LIST ||
      alarm?.details?.hotlist ||
      alarm?.details?.Hotlist ||
      alarm?.details?.HOTLIST ||
      alarm?.details?.hotlistName ||
      alarm?.details?.HotlistName ||
      alarm?.details?.['Hotlist Name'] ||
      alarm?.details?.listName ||
      alarm?.details?.details?.listName ||
      alarm?.details?.ListName ||
      alarm?.details?.['List Name'] ||
      (alarm?.source && 'name' in alarm.source ? alarm.source.name : null);

    const result = hotlistName || '';
    const formattedName = this.toTitleCase(result);

    // Hide hotlist name if it's "No Source Provided" or similar
    return this.shouldHideHotlistName(formattedName) ? '' : formattedName;
  }

  /**
   * Determines if a hotlist name should be hidden from display.
   */
  shouldHideHotlistName(hotlistName: string): boolean {
    if (!hotlistName || !hotlistName.trim()) return true;

    const normalizedName = hotlistName.toLowerCase().trim();
    return (
      normalizedName === 'no source provided' ||
      normalizedName === 'no source' ||
      normalizedName === 'unknown' ||
      normalizedName === 'n/a'
    );
  }

  /**
   * Extracts and formats hotlist category with title case.
   */
  getHotlistCategory(alarm: SnAlarm): string {
    const category =
      alarm?.details?.reason ||
      alarm?.details?.Reason ||
      alarm?.details?.REASON ||
      alarm?.details?.hotlistCategory ||
      alarm?.details?.details?.hotlistCategory ||
      alarm?.details?.category ||
      alarm?.details?.Category ||
      alarm?.details?.CATEGORY;
    const result = category || '';
    return this.toTitleCase(result);
  }

  /**
   * Private method that performs the actual computation of ALPR details.
   * Contains the original logic from getAlprDetails method.
   */
  private computeAlprDetails(alarm: SnAlarm): AlprDetails {
    const details = alarm.details || {};
    const nestedDetails = details.details || {};

    const { plateImageFromMedia, contextImageFromMedia } = this.extractImagesFromMedia(alarm);

    // Check for plateImageUrl at the alarm root level (same level as thumbnailUrl)
    const plateImageUrl = alarm.plateImageUrl;

    return {
      // LICENSE PLATE DATA
      plate: this.getLicensePlate(alarm),
      state:
        this.cleanAlprValue(details.plateState) ||
        this.cleanAlprValue(details['Plate State']) ||
        this.cleanAlprValue(details.state) ||
        this.cleanAlprValue(details.plate_state) ||
        this.cleanAlprValue(details.plateRegion) ||
        this.cleanAlprValue(details.best_region) ||
        this.cleanAlprValue(details['ori State']),
      reason:
        details.reason || details.alert_reason || details['Alert Type'] || nestedDetails.reason,

      // VEHICLE MAKE
      make:
        this.cleanAlprValue(details.vehicleMake) ||
        this.cleanAlprValue(details['Vehicle Make']) ||
        this.cleanAlprValue(details.make) ||
        this.cleanAlprValue(details.vehicle_make) ||
        this.cleanAlprValue(nestedDetails.vehicleMake) ||
        this.cleanAlprValue(nestedDetails.make),

      // VEHICLE MODEL
      model:
        this.cleanAlprValue(details.vehicleModel) ||
        this.cleanAlprValue(details['Vehicle Model']) ||
        this.cleanAlprValue(details.model) ||
        this.cleanAlprValue(details.vehicle_model) ||
        this.cleanAlprValue(nestedDetails.vehicleModel) ||
        this.cleanAlprValue(nestedDetails.model),

      // VEHICLE COLOR
      color:
        this.cleanAlprValue(details.vehicleColor) ||
        this.cleanAlprValue(details['Vehicle Color']) ||
        this.cleanAlprValue(details.color) ||
        this.cleanAlprValue(details.vehicle_color) ||
        this.cleanAlprValue(nestedDetails.vehicleColor) ||
        this.cleanAlprValue(nestedDetails.color),

      // VEHICLE TYPE/STYLE
      type:
        this.cleanAlprValue(details.vehicleBodyType) ||
        this.cleanAlprValue(details['Vehicle Style']) ||
        this.cleanAlprValue(details['Vehicle Type']) ||
        this.cleanAlprValue(details.vehicle_type) ||
        this.cleanAlprValue(details.vehicle_body_type) ||
        this.cleanAlprValue(details.type) ||
        this.cleanAlprValue(nestedDetails.vehicleBodyType) ||
        this.cleanAlprValue(nestedDetails.type),

      // VEHICLE YEAR
      year:
        this.cleanAlprValue(details.vehicleYear) ||
        this.cleanAlprValue(details['Vehicle Year']) ||
        this.cleanAlprValue(details['Plate Year']) ||
        this.cleanAlprValue(details.year) ||
        this.cleanAlprValue(details.vehicle_year) ||
        this.cleanAlprValue(nestedDetails.vehicleYear) ||
        this.cleanAlprValue(nestedDetails.year),

      // CONFIDENCE - Multiple variations found
      confidence:
        details.plateConfidence ||
        details.Confidence ||
        details.confidence ||
        details.plate_confidence ||
        nestedDetails.plateConfidence,

      // CAMERA INFO
      cameraName:
        details.cameraName ||
        details.camera_name ||
        details['Camera Name'] ||
        details.CameraName ||
        details.camera_label ||
        nestedDetails.cameraName,

      // IMAGES - Multiple variations found
      contextImage:
        details.contextImage ||
        details.context_image ||
        details.overview_jpeg ||
        details.Overview ||
        details.vehicle_crop_jpeg ||
        details.ContextImage ||
        details['Overview Image'] ||
        nestedDetails.contextImage ||
        contextImageFromMedia,

      plateImage:
        plateImageUrl ||
        details.plateImageUrl ||
        nestedDetails.plateImageUrl ||
        plateImageFromMedia ||
        details.plateImage ||
        details.plate_image ||
        details.Patch ||
        details.patch_image ||
        details.PlateImage ||
        details.plate_crop_jpeg ||
        details['Patch Image'] ||
        nestedDetails.plateImage,

      speed: details.speed || details.Speed || details.vehicle_speed || nestedDetails.speed,
      direction:
        details.direction ||
        details.Direction ||
        details.travel_direction ||
        nestedDetails.direction,

      // PROVIDER INFO
      provider: this.getAlprProvider(alarm),

      // CONFIDENCE FIELDS (Fusus/Flock/Axon/ and any of the "works with Axon" providers should have this)
      plateStateConfidence: details.plateStateConfidence,
      vehicleMakeConfidence: details.vehicleMakeConfidence,
      vehicleYearConfidence: details.vehicleYearConfidence,
      vehicleColorConfidence: details.vehicleColorConfidence,
      vehicleModelConfidence: details.vehicleModelConfidence,
      vehicleBodyTypeConfidence: details.vehicleBodyTypeConfidence,

      // BOUNDING BOX DATA
      plateBoundingBox: details.plateBoundingBox || nestedDetails.plateBoundingBox,
      imageCoordinates: details.imageCoordinates || nestedDetails.imageCoordinates,
    };
  }

  /**
   * Extracts plate and context images from the alarm's media array.
   * Used for providers that store images in a separate media array (e.g., Axon Fleet).
   */
  private extractImagesFromMedia(alarm: SnAlarm): {
    plateImageFromMedia: string | null;
    contextImageFromMedia: string | null;
  } {
    if (!alarm.media || !Array.isArray(alarm.media) || alarm.media.length === 0) {
      return { plateImageFromMedia: null, contextImageFromMedia: null };
    }

    let plateImageFromMedia: string | null = null;
    let contextImageFromMedia: string | null = null;

    for (const mediaItem of alarm.media) {
      if (!mediaItem.url) continue;

      const url = mediaItem.url.toLowerCase();

      if (url.includes('plate.jpeg') || url.includes('plate.jpg') || url.includes('plate.png')) {
        plateImageFromMedia = mediaItem.url;
      } else if (
        url.includes('context.jpeg') ||
        url.includes('context.jpg') ||
        url.includes('context.png')
      ) {
        contextImageFromMedia = mediaItem.url;
      }
    }

    return { plateImageFromMedia, contextImageFromMedia };
  }

  /**
   * Private method to cache results with LRU eviction.
   */
  private cacheAlprDetailsResult(key: string, result: AlprDetails): void {
    // Implement LRU eviction - remove oldest entry if at max capacity
    if (this.alprDetailsCache.size >= this.maxCacheSize) {
      const firstKey = this.alprDetailsCache.keys().next().value;
      this.alprDetailsCache.delete(firstKey);
    }

    this.alprDetailsCache.set(key, result);
  }

  /**
   * Converts a string to title case (first letter of each word capitalized).
   */
  private toTitleCase(str: string): string {
    if (!str) return str;
    return startCase(toLower(str));
  }
}
